import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { stringFormattingToCurrency } from "js/formatting";
import {
  isAllowedToMakeCCPayment,
  isFlagEnabled,
  getAutoPayStatus,
  isOldAlicUlPolicy,
  isPolicyInactiveByPolicyStatus,
  isPolicyInGrace,
} from "js/utilities";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import Tooltip from "components/General/Tooltip";

const PaymentSummaryCard = ({ policyDetail }) => {
  const { t } = useTranslation();
  const uuid = uuidv4();
  useCollapsePanel(uuid);
  const isOldAlicUl = isOldAlicUlPolicy(policyDetail);
  const isEftAutopayEnrolled = policyDetail?.billingTypeCode === "02";
  const policyType = policyDetail?.product?.type?.trim().toUpperCase();
  const hidePaymentStatus =
    policyType === "LUE" || policyType === "LU" || policyType === "LZ";

  return (
    <>
      <div className="ui-card">
        <div className="p-4 xl:pt-6 xl:px-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label={t(
                  "AgentPaymentSummaryCard-expand-collapse-btn-label"
                )}
              >
                <span className="inline-block p-1 rounded-full mr-1 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span
                  className={`text-base font-body font-bold md:ui-no-badge-icon-inline`}
                >
                  {t("AgentPaymentSummaryCard-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("AgentPaymentSummaryCard-title")}
              </span>
            </h2>
          </div>
        </div>
        <hr className="ui-mobile-collapse" />
        <div
          id={`${uuid}-panel`}
          className="overflow-hidden h-20 p-4 md:h-auto xl:p-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
            {!hidePaymentStatus && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentPaymentSummaryCard-label-1")}
                </div>
                <div className="flex text-sm text-theme-text-secondary font-semibold">
                  <p>{policyDetail?.paymentSummary?.paymentStatus}</p>
                  {policyDetail?.paymentSummary?.paymentStatus?.toLowerCase() ===
                    "returned" &&
                    isFlagEnabled("ENABLE_TOOLTIP_FOR_RETURNED_EFT_STATUS") && (
                      <Tooltip
                        xMargin="mx-0"
                        placement="right"
                        content={
                          <>
                            {t("AgentPaymentSummaryCard-tooltip")}{" "}
                            <Link
                              className="underline text-theme-action"
                              to={`/messages?tab=correspondence&id=${policyDetail.oldPolicy}`}
                            >
                              {t(
                                "AgentPaymentSummaryCard-Alerts-Correspondence"
                              )}
                            </Link>
                            .
                          </>
                        }
                      />
                    )}
                </div>
              </div>
            )}
            {isOldAlicUl && !isEftAutopayEnrolled ? null : (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {isOldAlicUl && isEftAutopayEnrolled
                    ? t(
                        "AgentPaymentSummaryCard-payment-summary-planned-premium-date"
                      )
                    : t("AgentPaymentSummaryCard-label-4")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {isOldAlicUl && isEftAutopayEnrolled
                    ? policyDetail?.paymentSummary?.dueDate
                    : stringFormattingToCurrency(
                        policyDetail?.paymentSummary?.amount
                      )}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {isOldAlicUl
                  ? t(
                      "AgentPaymentSummaryCard-payment-summary-planned-payment-frequency"
                    )
                  : t("AgentPaymentSummaryCard-label-6")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {isOldAlicUl
                  ? policyDetail?.paymentSummary?.frequency
                  : policyDetail?.lastPaymentDate}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {isOldAlicUl
                  ? t(
                      "AgentPaymentSummaryCard-payment-summary-planned-premium-amount"
                    )
                  : t("AgentPaymentSummaryCard-label-2")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {isOldAlicUl
                  ? stringFormattingToCurrency(
                      policyDetail?.paymentSummary?.amount
                    )
                  : policyDetail?.paymentSummary?.dueDate}
              </div>
            </div>
            {!isOldAlicUl && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentPaymentSummaryCard-label-3")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {policyDetail?.paymentSummary?.frequency}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("AgentPaymentSummaryCard-label-5")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {policyDetail?.paymentSummary?.method}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("AgentPaymentSummaryCard-label-7")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {getAutoPayStatus(policyDetail)}
              </div>
            </div>

            {isOldAlicUl ? (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentPaymentSummaryCard-label-6")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {policyDetail?.lastPaymentDate}
                </div>
              </div>
            ) : (
              <>
                {!isPolicyInactiveByPolicyStatus(policyDetail?.status) &&
                  isPolicyInGrace(policyDetail) && (
                    <div>
                      <div className="text-xs text-theme-text-tertiary">
                        {t("AgentPaymentSummaryCard-label-8")}
                      </div>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        {moment(
                          policyDetail?.financials?.cashValues?.find(
                            (cashValue) =>
                              moment(cashValue.projectedlapseDate).isAfter(
                                moment()
                              )
                          )?.projectedlapseDate
                        ).format("MM/DD/YYYY")}
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        {isOldAlicUl ? (
          <CardDisclaimerBase
            disclaimer={t(
              "AgentPaymentSummaryCard-oldAlicUl-payment-summary-disclaimer"
            )}
          />
        ) : (
          !isAllowedToMakeCCPayment(policyDetail, "onetimepayment") && (
            <CardDisclaimerBase
              disclaimer={t("AgentPaymentSummaryCard-disclaimer")}
            />
          )
        )}
      </div>
    </>
  );
};

export default PaymentSummaryCard;
